// ProcessSettings — the school's recruitment process as one versioned setting
// (Nilay Sharma's proposal pr_d388d3271a): each track's stage weights, the
// pass mark and the strong-subject threshold. A recruitment administrator
// proposes a new version; a DIFFERENT administrator puts it into force; a
// version in force is never edited (GET /api/hiring-process, POST
// …/versions, POST …/versions/:v/approve). Below it, the exit filter's named
// conductors (GET / PUT /api/exit-filter/conductors).
// @story HIR-GL1

import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Input, Select, StatusPill } from '../../ds';
import { Loading, Note } from '../../components/page';

const STAGES = [
  ['cv', 'CV'], ['written', 'Written test'], ['demo', 'Demo'], ['admin_round', 'Admin round'],
  ['preliminary', 'Preliminary interview'], ['interview2_p1', 'Interview 2, phase 1'], ['interview2_p2', 'Interview 2, phase 2'],
];
const TRACKS = [['teaching', 'Teaching'], ['non-teaching', 'Non-teaching'], ['leadership', 'Leadership']];
const muted = { color: 'var(--text-muted)', fontSize: 'var(--fs-caption)' };
const cell = { padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 'var(--fs-body-sm)', textAlign: 'right' };
const head = { ...cell, fontWeight: 600, color: 'var(--text-1)' };
const when = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const sumOf = (w) => Object.values(w || {}).reduce((a, n) => a + (Number(n) || 0), 0);

function isAdmin() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u.role === 'admin' || (u.permissions || []).includes('recruitment:admin');
  } catch { return false; }
}

function WeightsTable({ tracks, edit, onChange }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ ...head, textAlign: 'left' }}>Stage</th>
            {TRACKS.map(([t, label]) => <th key={t} style={head}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {STAGES.map(([k, label]) => (
            <tr key={k}>
              <td style={{ ...cell, textAlign: 'left' }}>{label}</td>
              {TRACKS.map(([t]) => (
                <td key={t} style={cell}>
                  {edit && (t !== 'leadership' || tracks.leadership) ? (
                    <span style={{ display: 'inline-block', width: 70 }}>
                      <Input type="number" aria-label={`${t} ${label}`} value={tracks[t]?.[k] ?? 0}
                        onChange={(e) => onChange(t, k, Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))))} />
                    </span>
                  ) : (tracks[t] ? (tracks[t][k] ?? '—') : (k === 'cv' ? 'not decided' : ''))}
                </td>
              ))}
            </tr>
          ))}
          <tr>
            <td style={{ ...head, textAlign: 'left' }}>Total</td>
            {TRACKS.map(([t]) => {
              const s = sumOf(tracks[t]);
              return <td key={t} style={head}>{tracks[t] ? <StatusPill tone={s === 100 ? 'success' : 'danger'}>{s}</StatusPill> : '—'}</td>;
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Conductors({ admin }) {
  const [list, setList] = useState(null);
  const [people, setPeople] = useState([]);
  const [pick, setPick] = useState('');
  const [msg, setMsg] = useState(null);
  const load = useCallback(() => {
    api.get('/exit-filter/conductors').then((r) => setList(r.data?.data || [])).catch(() => setList(false));
  }, []);
  useEffect(() => {
    load();
    if (admin) api.get('/human-interview/panel-people').then((r) => setPeople(r.data?.data || [])).catch(() => {});
  }, [admin, load]);
  const save = (userIds) => {
    setMsg(null);
    api.put('/exit-filter/conductors', { userIds })
      .then((r) => { setList(r.data?.data || []); setPick(''); setMsg({ tone: 'success', text: 'Saved.' }); })
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not save. Try again.' }));
  };
  if (list === null) return <Loading label="Reading the conductors" />;
  if (list === false) return <Note tone="warning">The conductors could not be read just now.</Note>;
  const ids = list.map((c) => c.userId);
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <p style={{ margin: 0, color: 'var(--text-2)' }}>
        The people who decide for a candidate below a stage's pass mark: return, redirect or reject, with a reason. HR sees the queue but cannot decide.
      </p>
      {list.length ? (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {list.map((c) => (
            <li key={c.userId} style={{ fontSize: 'var(--fs-body-sm)' }}>
              {c.name || c.email} <span style={muted}>{c.email}</span>
              {admin && <Button size="sm" variant="ghost" onClick={() => save(ids.filter((x) => x !== c.userId))}>Remove</Button>}
            </li>
          ))}
        </ul>
      ) : <Note tone="warning">No conductors are named yet, so nobody can decide in the exit filter.</Note>}
      {admin && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 260 }}>
            <Select label="Add a conductor" value={pick} onChange={setPick} placeholder="Choose a person…"
              options={people.filter((p) => !ids.includes(p.userId)).map((p) => ({ value: p.userId, label: p.label || `${p.name} (${p.email})` }))} />
          </div>
          <Button onClick={() => save([...ids, pick])} disabled={!pick}>Add</Button>
        </div>
      )}
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
    </div>
  );
}

export default function ProcessSettings() {
  const admin = isAdmin();
  const [st, setSt] = useState({ phase: 'loading' });
  const [draft, setDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get('/hiring-process')
      .then((r) => setSt({ phase: 'ready', ...r.data.data }))
      .catch(() => setSt({ phase: 'error' }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    const c = st.current;
    setDraft({ tracks: JSON.parse(JSON.stringify(c.tracks)), passMark: c.passMark, strongThreshold: c.strongThreshold, note: '' });
    setPreview(null); setMsg(null);
  };
  const body = () => ({ ...draft, tracks: Object.fromEntries(Object.entries(draft.tracks).filter(([, w]) => w)) });
  const check = () => {
    setBusy(true); setMsg(null);
    api.post('/hiring-process/versions', { ...body(), preview: true })
      .then((r) => setPreview(r.data.data))
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not check that.' }))
      .finally(() => setBusy(false));
  };
  const propose = () => {
    setBusy(true);
    api.post('/hiring-process/versions', body())
      .then((r) => { setDraft(null); setPreview(null); setMsg({ tone: 'success', text: `Version ${r.data.data.version} is waiting for a different administrator to approve it.` }); load(); })
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not save that.' }))
      .finally(() => setBusy(false));
  };
  const approve = (v) => {
    if (!window.confirm(`Put version ${v} into force? Totals read from now on use it. It cannot be edited afterwards.`)) return;
    api.post(`/hiring-process/versions/${v}/approve`)
      .then(() => { setMsg({ tone: 'success', text: `Version ${v} is now in force.` }); load(); })
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not approve it.' }));
  };

  if (st.phase === 'loading') return <Loading label="Reading the recruitment process" />;
  if (st.phase === 'error') return <Note tone="warning">The recruitment process could not be read just now. Reload the page to try again.</Note>;
  const c = st.current;
  const waiting = (st.versions || []).filter((v) => v.status === 'draft');

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <strong>Version {c.version} in force</strong>
          {c.builtIn ? <span style={muted}>(the group's process as written; not yet approved at this school)</span>
            : <span style={muted}>approved by {c.approvedBy?.name} on {when(c.approvedAt)}, proposed by {c.proposedBy?.name}</span>}
        </div>
        <div style={{ fontSize: 'var(--fs-body-sm)' }}>Pass mark <strong>{c.passMark}%</strong> at every stage · strong-subject threshold <strong>{c.strongThreshold}%</strong></div>
        <WeightsTable tracks={c.tracks} />
        {!c.leadershipDecided && <Note tone="info">Leadership weights are not decided yet, so leadership posts get no total.</Note>}
        <div style={muted}>A Day at DPS, the Society round, the exit filter and the AI final interview carry no weight.</div>
      </div>

      {waiting.map((v) => (
        <Note key={v.version} tone="warning">
          Version {v.version}, proposed by {v.proposedBy?.name} on {when(v.proposedAt)}, is waiting for approval.
          {v.note ? ` "${v.note}"` : ''}{' '}
          {admin && <Button size="sm" onClick={() => approve(v.version)}>Approve version {v.version}</Button>}
        </Note>
      ))}

      {admin && !draft && <div><Button variant="secondary" onClick={startEdit}>Propose a new version</Button></div>}
      {draft && (
        <div style={{ display: 'grid', gap: 10, border: '1px solid var(--border)', borderRadius: 8, padding: 12 }}>
          <strong>New version</strong>
          <WeightsTable tracks={draft.tracks} edit onChange={(t, k, n) => setDraft((d) => ({ ...d, tracks: { ...d.tracks, [t]: { ...d.tracks[t], [k]: n } } }))} />
          <label style={{ fontSize: 'var(--fs-body-sm)', display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={Boolean(draft.tracks.leadership)}
              onChange={(e) => setDraft((d) => ({ ...d, tracks: { ...d.tracks, leadership: e.target.checked ? { cv: 10 } : undefined } }))} />
            Leadership weights are decided
          </label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ width: 160 }}><Input label="Pass mark %" type="number" value={draft.passMark} onChange={(e) => setDraft((d) => ({ ...d, passMark: Number(e.target.value) }))} /></span>
            <span style={{ width: 200 }}><Input label="Strong-subject threshold %" type="number" value={draft.strongThreshold} onChange={(e) => setDraft((d) => ({ ...d, strongThreshold: Number(e.target.value) }))} /></span>
          </div>
          <Input label="Why, and on whose decision" value={draft.note} onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} />
          {preview && (
            <Note tone="info">
              Version {preview.version} would change: {preview.changes.map((x) => x.what).join(', ')}. Nothing changes for any candidate until a different administrator approves it.
            </Note>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            {!preview ? <Button onClick={check} disabled={busy}>Check</Button> : <Button onClick={propose} disabled={busy}>Send for approval</Button>}
            <Button variant="ghost" onClick={() => { setDraft(null); setPreview(null); }}>Cancel</Button>
          </div>
        </div>
      )}
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}

      <div style={{ display: 'grid', gap: 8 }}>
        <strong>Exit-filter conductors</strong>
        <Conductors admin={admin} />
      </div>

      {(st.versions || []).length > 1 && (
        <details>
          <summary style={{ cursor: 'pointer', fontSize: 'var(--fs-body-sm)' }}>Every version</summary>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {st.versions.map((v) => (
              <li key={v.version} style={{ fontSize: 'var(--fs-body-sm)' }}>
                Version {v.version}: {v.status}{v.builtIn ? ' (built in)' : ''}
                {v.proposedBy ? `, proposed by ${v.proposedBy.name} on ${when(v.proposedAt)}` : ''}
                {v.approvedBy ? `, approved by ${v.approvedBy.name} on ${when(v.approvedAt)}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
